import { useState } from "react";

import {
  ArrowSquareOut,
  CaretDown,
  CaretRight,
  EyeSlash,
  SealCheck,
  X,
} from "@phosphor-icons/react";
import {
  Badge,
  Button,
  DataRow,
  DataTable,
  LoadingOverlay,
  PaginationFooter,
  ProfileTabs,
  VerifiedBadge,
  useToast,
} from "@breakingthelines/design-system";
import { Link } from "react-router-dom";

import { handleTabArrowKeyDown } from "@/components/common/tab-keyboard-nav";
import { siteBaseUrl } from "@/config/env";
import {
  useHideAllWriterContent,
  useNewWriterArticles,
  useNewWriters,
  useVerifyWriter,
} from "@/hooks/use-new-writers";
import type { NewWriter, NewWriterWindow } from "@/lib/admin-api";

import styles from "./new-writers.module.css";

const WINDOW_TABS = ["7 days", "30 days", "90 days"] as const;
type WindowTab = (typeof WINDOW_TABS)[number];

const WINDOW_PARAM: Record<WindowTab, NewWriterWindow> = {
  "7 days": "7d",
  "30 days": "30d",
  "90 days": "90d",
};

const PAGE_SIZE = 20;

function formatDate(value?: string): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
  }).format(date);
}

/**
 * Turns content_judge_log's tier into what it means to an operator. Both LOW
 * and MEDIUM queue the same way for a human (a flag, no automatic action), so
 * they read as one state here. HIGH is auto-deranked. An empty tier means the
 * async judge has not reached this article yet.
 */
function judgeTierLabel(tier: string): string {
  switch (tier) {
    case "NONE":
      return "Looks fine";
    case "LOW":
    case "MEDIUM":
      return "Needs review";
    case "HIGH":
      return "Spam";
    default:
      return "Not reviewed yet";
  }
}

function judgeTierClass(tier: string): string {
  switch (tier) {
    case "NONE":
      return styles.judgeClean;
    case "LOW":
    case "MEDIUM":
      return styles.judgeReview;
    case "HIGH":
      return styles.judgeSpam;
    default:
      return styles.judgeUnknown;
  }
}

function moderationStatusLabel(status: string): string {
  switch (status) {
    case "MODERATION_STATUS_HIDDEN":
      return "Hidden";
    case "MODERATION_STATUS_REMOVED":
      return "Removed";
    case "MODERATION_STATUS_DERANKED":
      return "Reach reduced";
    default:
      return "Visible";
  }
}

function visibilityLabel(value: string): string {
  switch (value) {
    case "VISIBILITY_LEVEL_PUBLIC":
      return "Public";
    case "VISIBILITY_LEVEL_UNLISTED":
      return "Unlisted";
    case "VISIBILITY_LEVEL_SUBSCRIBERS_ONLY":
      return "Subscribers only";
    case "VISIBILITY_LEVEL_TIER_GATED":
      return "Tier gated";
    default:
      return "";
  }
}

function writerLabel(writer: NewWriter): string {
  if (writer.handle) return `@${writer.handle}`;
  return writer.displayName || writer.creatorId;
}

interface WriterArticlesProps {
  creatorId: string;
  window: NewWriterWindow;
}

function WriterArticles({ creatorId, window: activeWindow }: WriterArticlesProps) {
  const { articles, loading, failed } = useNewWriterArticles(creatorId, activeWindow, true);

  if (loading) return <LoadingOverlay label="Loading articles" />;
  if (failed)
    return <div className={styles.emptyState}>Could not load this writer's articles.</div>;
  if (articles.length === 0) {
    return <div className={styles.emptyState}>No articles in this window.</div>;
  }

  return (
    <DataTable
      columns="minmax(220px, 1.8fr) minmax(100px, 0.7fr) minmax(110px, 0.8fr) minmax(70px, 0.5fr) minmax(120px, 0.8fr) minmax(110px, 0.8fr)"
      header={["Article", "Published", "Visibility", "Words", "Judge", "Status"]}
      headerClassName={styles.articleTableHeader}
      label={`Articles by ${creatorId}`}
    >
      {articles.map((article) => (
        <DataRow key={article.contentId} className={styles.articleRow}>
          <div className={styles.articleTitleCell} data-label="Article">
            {article.canonicalPath ? (
              <a
                href={`${siteBaseUrl()}${article.canonicalPath}`}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.articleLink}
              >
                {article.title || article.contentId}
              </a>
            ) : (
              <span>{article.title || article.contentId}</span>
            )}
            <Link to={`/content/${article.contentId}`} className={styles.adminLink}>
              Admin <ArrowSquareOut size={12} />
            </Link>
          </div>
          <span className={styles.cellText} data-label="Published">
            {formatDate(article.publishedAt)}
          </span>
          <span className={styles.cellText} data-label="Visibility">
            {visibilityLabel(article.visibility)}
          </span>
          <span className={styles.cellText} data-label="Words">
            {article.wordCount.toLocaleString()}
          </span>
          <span
            className={`${styles.judgeChip} ${judgeTierClass(article.judgeTier)}`}
            data-label="Judge"
          >
            {judgeTierLabel(article.judgeTier)}
          </span>
          <span className={styles.cellText} data-label="Status">
            {moderationStatusLabel(article.moderationStatus)}
          </span>
        </DataRow>
      ))}
    </DataTable>
  );
}

export function NewWritersPage() {
  const toast = useToast();
  const [windowTab, setWindowTab] = useState<WindowTab>("30 days");
  const [page, setPage] = useState(1);
  const [expandedCreatorId, setExpandedCreatorId] = useState<string | null>(null);
  const [hideTarget, setHideTarget] = useState<NewWriter | null>(null);
  const [hideNote, setHideNote] = useState("");

  const activeWindow = WINDOW_PARAM[windowTab];
  const { writers, pageInfo, loading, failed } = useNewWriters(activeWindow, page, PAGE_SIZE);
  const verifyMutation = useVerifyWriter();
  const hideMutation = useHideAllWriterContent();

  const totalCount = pageInfo?.totalCount ?? writers.length;
  const totalLabel = `Writers: ${totalCount}`;
  const totalPages = totalCount > 0 ? Math.max(1, Math.ceil(totalCount / PAGE_SIZE)) : page;
  const activePage = Math.min(page, totalPages);
  const pages = Array.from({ length: totalPages }, (_, i) => i + 1);
  const hasNext = pageInfo?.hasNextPage ?? (writers.length === PAGE_SIZE && writers.length > 0);

  function toggleExpanded(creatorId: string) {
    setExpandedCreatorId((current) => (current === creatorId ? null : creatorId));
  }

  async function handleVerify(writer: NewWriter) {
    try {
      await verifyMutation.mutateAsync({ userId: writer.creatorId, verified: true });
      toast.success("Their articles are now indexable.", "Verified");
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to verify this writer",
        "Couldn't verify",
      );
    }
  }

  function openHideDialog(writer: NewWriter) {
    setHideNote("");
    setHideTarget(writer);
  }

  function closeHideDialog() {
    setHideTarget(null);
    setHideNote("");
  }

  async function handleConfirmHide() {
    if (!hideTarget) return;
    try {
      const result = await hideMutation.mutateAsync({
        creatorId: hideTarget.creatorId,
        note: hideNote.trim(),
      });
      toast.success(
        `Hidden ${result.contentAffected ?? 0} article(s). Direct links still work.`,
        "Hidden",
      );
      closeHideDialog();
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to hide this writer's content",
        "Couldn't hide content",
      );
    }
  }

  return (
    <div className={styles.page}>
      <div>
        <h1 className={styles.title}>New writers</h1>
        <p className={styles.subtitle}>
          Writers whose first published article landed in this window, newest first.
        </p>
      </div>

      <div className={styles.toolbar}>
        <ProfileTabs
          role="tablist"
          aria-label="Window"
          tabs={WINDOW_TABS.map((tab) => ({ id: tab, label: tab }))}
          activeTab={windowTab}
          onKeyDown={handleTabArrowKeyDown}
          onTabChange={(tab) => {
            setWindowTab(tab as WindowTab);
            setPage(1);
            setExpandedCreatorId(null);
          }}
        />
      </div>

      <section className={styles.tableSection}>
        {loading ? <LoadingOverlay label="Loading writers" /> : null}

        <DataTable
          columns="minmax(220px, 1.6fr) minmax(100px, 0.7fr) minmax(100px, 0.7fr) minmax(70px, 0.5fr) minmax(90px, 0.6fr) minmax(220px, 1.6fr) minmax(70px, 0.5fr) minmax(220px, 1.6fr)"
          header={[
            "Writer",
            "Created",
            "First published",
            "Articles",
            "Busiest day",
            "Judge",
            "Hidden",
            "Actions",
          ]}
          headerClassName={styles.tableHeader}
          label="New writers"
        >
          {writers.flatMap((writer) => {
            const expanded = expandedCreatorId === writer.creatorId;
            const rows = [
              <DataRow
                key={writer.creatorId}
                className={styles.tableRow}
                onActivate={() => toggleExpanded(writer.creatorId)}
              >
                <div className={styles.writerCell} data-label="Writer">
                  {expanded ? <CaretDown size={14} /> : <CaretRight size={14} />}
                  <div className={styles.writerIdentity}>
                    <span className={styles.writerHandle}>
                      {writerLabel(writer)}
                      {writer.isVerified ? <VerifiedBadge size="sm" /> : null}
                    </span>
                    {writer.displayName && writer.handle ? (
                      <span className={styles.writerDisplayName}>{writer.displayName}</span>
                    ) : null}
                    {writer.isAuthorizedPublisher ? (
                      <Badge className={styles.authorizedBadge}>Authorized publisher</Badge>
                    ) : null}
                  </div>
                </div>
                <span className={styles.cellText} data-label="Created">
                  {formatDate(writer.accountCreatedAt)}
                </span>
                <span className={styles.cellText} data-label="First published">
                  {formatDate(writer.firstPublishedAt)}
                </span>
                <span className={styles.cellText} data-label="Articles">
                  {writer.articleCount}
                </span>
                <span className={styles.cellText} data-label="Busiest day">
                  {writer.busiestDayCount}
                </span>
                <div className={styles.judgeSummaryCell} data-label="Judge">
                  <span className={`${styles.judgeChip} ${styles.judgeClean}`}>
                    {writer.cleanCount} looks fine
                  </span>
                  <span className={`${styles.judgeChip} ${styles.judgeReview}`}>
                    {writer.needsReviewCount} needs review
                  </span>
                  <span className={`${styles.judgeChip} ${styles.judgeSpam}`}>
                    {writer.spamCount} spam
                  </span>
                </div>
                <span className={styles.cellText} data-label="Hidden">
                  {writer.moderatedCount}
                </span>
                <div
                  className={styles.actions}
                  onClick={(event) => event.stopPropagation()}
                  onKeyDown={(event) => event.stopPropagation()}
                >
                  {writer.isVerified ? null : (
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      disabled={verifyMutation.isPending}
                      onClick={() => void handleVerify(writer)}
                    >
                      <SealCheck size={14} /> Verify
                    </Button>
                  )}
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    onClick={() => openHideDialog(writer)}
                  >
                    <EyeSlash size={14} /> Hide all content
                  </Button>
                </div>
              </DataRow>,
            ];

            if (expanded) {
              rows.push(
                <DataRow key={`${writer.creatorId}-expanded`} className={styles.expandedRow}>
                  <div className={styles.expandedCell}>
                    <WriterArticles creatorId={writer.creatorId} window={activeWindow} />
                  </div>
                </DataRow>,
              );
            }

            return rows;
          })}
        </DataTable>

        {!loading && failed ? (
          <div className={styles.emptyState}>Could not load new writers.</div>
        ) : null}
        {!loading && !failed && writers.length === 0 ? (
          <div className={styles.emptyState}>No writers started publishing in this window.</div>
        ) : null}
      </section>

      <PaginationFooter
        totalLabel={totalLabel}
        pages={pages}
        activePage={activePage}
        hasPrevious={page > 1}
        hasNext={hasNext}
        onPrevious={() => setPage((p) => Math.max(1, p - 1))}
        onNext={() => setPage((p) => p + 1)}
        onPageChange={(target) => {
          if (target === activePage) return;
          setPage(target);
        }}
        className={styles.pagination}
      />

      {hideTarget ? (
        <div className={styles.dialogOverlay} onMouseDown={() => closeHideDialog()}>
          <section
            role="dialog"
            aria-modal="true"
            aria-labelledby="hide-writer-title"
            className={styles.dialogContent}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <header className={styles.dialogHeader}>
              <div>
                <h2 id="hide-writer-title" className={styles.dialogTitle}>
                  Hide all content
                </h2>
                <p className={styles.dialogDescription}>
                  Hides every article {writerLabel(hideTarget)} has published. Direct links still
                  work. This does not remove anything.
                </p>
              </div>
              <button
                type="button"
                className={styles.dialogClose}
                onClick={() => closeHideDialog()}
                aria-label="Close"
                disabled={hideMutation.isPending}
              >
                <X size={14} weight="bold" />
              </button>
            </header>

            <div className={styles.dialogBody}>
              <form
                className={styles.dialogForm}
                onSubmit={(event) => {
                  event.preventDefault();
                  void handleConfirmHide();
                }}
              >
                <label htmlFor="hide-note" className={styles.dialogLabel}>
                  Reason
                </label>
                <textarea
                  id="hide-note"
                  value={hideNote}
                  onChange={(event) => setHideNote(event.currentTarget.value)}
                  className={styles.dialogTextarea}
                  placeholder="Why is this writer's content being hidden?"
                  rows={3}
                />
              </form>
            </div>

            <footer className={styles.dialogFooter}>
              <Button
                type="button"
                variant="secondary"
                disabled={hideMutation.isPending}
                onClick={() => closeHideDialog()}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="destructive"
                disabled={hideMutation.isPending || hideNote.trim().length === 0}
                onClick={() => void handleConfirmHide()}
              >
                {hideMutation.isPending ? "Hiding…" : "Hide all content"}
              </Button>
            </footer>
          </section>
        </div>
      ) : null}
    </div>
  );
}
